import { createTheme, ThemeOptions } from '@mui/material/styles';

// Cores customizadas do sistema
export const colors = {
  primary: {
    main: '#667eea',
    light: '#8fa5f4',
    dark: '#4c6bd8',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#764ba2',
    light: '#9b6db5',
    dark: '#5c3880',
    contrastText: '#ffffff',
  },
  gradient: {
    primary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    primaryHover: 'linear-gradient(135deg, #5a67d8 0%, #6b46a3 100%)',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backgroundDark: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
  },
  background: {
    default: '#f8fafc',
    paper: '#ffffff',
    glassmorphism: 'rgba(255, 255, 255, 0.95)',
    glassmorphismDark: 'rgba(255, 255, 255, 0.05)',
  },
  shadow: {
    glass: '0 20px 40px rgba(0,0,0,0.1), 0 0 0 1px rgba(255,255,255,0.5)',
    glassDark: '0 20px 40px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1)',
    button: '0 8px 16px rgba(102, 126, 234, 0.3)',
    buttonHover: '0 12px 24px rgba(102, 126, 234, 0.4)',
  }
};

// Configurações de espaçamento
export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px',
};

// Configurações de borderRadius
export const borderRadius = {
  small: '8px',
  medium: '12px',
  large: '16px',
  circle: '50%',
};

// Tema customizado
const themeOptions: ThemeOptions = {
  palette: {
    primary: colors.primary,
    secondary: colors.secondary,
    background: colors.background,
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.5,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.medium,
          textTransform: 'none',
          fontWeight: 600,
          padding: '12px 24px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          background: colors.gradient.primary,
          boxShadow: colors.shadow.button,
          '&:hover': {
            background: colors.gradient.primaryHover,
            boxShadow: colors.shadow.buttonHover,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.large,
          boxShadow: colors.shadow.glass,
          backdropFilter: 'blur(20px)',
          background: colors.background.glassmorphism,
          border: 'none',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.medium,
          backgroundColor: colors.background.paper,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary.main,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.medium,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          marginBottom: spacing.sm,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: borderRadius.large,
          backdropFilter: 'blur(20px)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: colors.background.glassmorphism,
          backdropFilter: 'blur(20px)',
        },
      },
    },
  },
};

export const theme = createTheme(themeOptions);